import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ShieldCheck, ChevronRight, CheckCircle2 } from "lucide-react";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLeadConversion } from "@/lib/conversions";
import { useLocale } from "@/lib/i18n/locale";
import {
  useGoogleAdsParams,
  GOOGLE_ADS_FIELD_IDS,
  GOOGLE_ADS_CONTACT_TAG,
  type GoogleAdsParams,
} from "@/hooks/useGoogleAdsParams";

interface SaintEligibilityCheckProps {
  /** Stable CRM form slug + GA4 form id. */
  source?: string;
  /** Heading shown above the form. */
  heading?: string;
  /** Subheading shown under the heading. */
  subheading?: string;
  /** Where to redirect after a successful submit. */
  redirectTo?: string;
  /** When true, tags the lead as Google Ads and appends gclid/utm params. */
  googleAds?: boolean;
  /** Compact card shell (default) or bare inline block. */
  variant?: "card" | "inline";
}

const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

/** Locale-aware label strings for the eligibility form. */
function useEligibilityStrings() {
  const { isEs } = useLocale();
  if (isEs) {
    return {
      q1: "¿Le han diagnosticado depresión?",
      q1Opts: [
        { value: "yes", label: "Sí" },
        { value: "no", label: "No" },
        { value: "not-sure", label: "No estoy seguro" },
      ],
      q2: "¿Ha probado medicación antidepresiva sin suficiente alivio?",
      q2Opts: [
        { value: "yes", label: "Sí" },
        { value: "no", label: "No" },
      ],
      q3: "¿En qué estado vive? *",
      statePlaceholder: "Seleccione su estado",
      firstName: "Nombre *",
      firstNamePlaceholder: "María",
      phone: "Teléfono *",
      phonePlaceholder: "(555) 123-4567",
      email: "Correo electrónico *",
      emailPlaceholder: "maria@ejemplo.com",
      consent:
        "Acepto ser contactado sobre el tratamiento SAINT® y he leído la ",
      privacyPolicy: "Política de privacidad",
      consentEnd: ". Nunca compartimos su información.",
      back: "Atrás",
      continue: "Continuar",
      submit: "Verificar mi elegibilidad",
      sending: "Enviando…",
      disclaimer:
        "SAINT® está autorizado por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. La cobertura puede estar disponible; verificamos los beneficios sin costo. Los resultados individuales pueden variar.",
      formLabels: {
        first_name: "Nombre",
        phone: "Teléfono",
        email: "Correo electrónico",
        calendar_notes: "Respuestas de elegibilidad",
      },
      notesTemplate: (d: string, t: string, s: string) =>
        `Diagnosticado con depresión: ${d}; Probado antidepresivos sin suficiente alivio: ${t}; Estado: ${s}`,
    };
  }
  return {
    q1: "Have you been diagnosed with depression?",
    q1Opts: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "not-sure", label: "Not sure" },
    ],
    q2: "Have you tried antidepressant medication without enough relief?",
    q2Opts: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    q3: "Which state do you live in? *",
    statePlaceholder: "Select your state",
    firstName: "First name *",
    firstNamePlaceholder: "Jane",
    phone: "Phone *",
    phonePlaceholder: "(555) 123-4567",
    email: "Email *",
    emailPlaceholder: "jane@example.com",
    consent:
      "I agree to be contacted about SAINT® treatment and have read the ",
    privacyPolicy: "Privacy Policy",
    consentEnd: ". We never share your information.",
    back: "Back",
    continue: "Continue",
    submit: "Check My Eligibility",
    sending: "Sending…",
    disclaimer:
      "SAINT® is FDA-cleared for adults with major depressive disorder who haven't improved on antidepressants. Coverage may be covered; we verify benefits free. Individual results may vary.",
    formLabels: {
      first_name: "First name",
      phone: "Phone",
      email: "Email",
      calendar_notes: "Eligibility answers",
    },
    notesTemplate: (d: string, t: string, s: string) =>
      `Diagnosed with depression: ${d}; Tried antidepressants without enough relief: ${t}; State: ${s}`,
  };
}

/**
 * 3-step SAINT® eligibility check.
 *
 * 1) "Have you been diagnosed with depression?" — Yes / No / Not sure
 * 2) "Have you tried antidepressant medication without enough relief?" — Yes / No
 * 3) "Which state do you live in?" (dropdown) + name, phone, email, consent
 *
 * On submit it fires generate_lead (GA4) + the Google Ads "SAINT Lead Form
 * Submit" conversion + the Meta Pixel `Lead` event EXACTLY ONCE via
 * trackLeadConversion — never on start, page view, or the thank-you page.
 * gclid/UTM/lead-source are passed through as Google Ads custom fields and
 * the three eligibility answers are written into the contact as notes.
 */
export const SaintEligibilityCheck: React.FC<SaintEligibilityCheckProps> = ({
  source = "saint-eligibility",
  heading,
  subheading,
  redirectTo,
  googleAds = false,
  variant = "card",
}) => {
  const navigate = useNavigate();
  const { isEs } = useLocale();
  const adsParams = useGoogleAdsParams();
  const s = useEligibilityStrings();

  const finalHeading =
    heading ??
    (isEs
      ? "Descubra si SAINT® es adecuado para usted"
      : "See if SAINT® is right for you");
  const finalSubheading =
    subheading ??
    (isEs
      ? "Tres preguntas rápidas. Sin compromiso. Una respuesta real de nuestro equipo clínico."
      : "Three quick questions. No obligation. A real answer from our clinical team.");
  const finalRedirectTo =
    redirectTo ?? (isEs ? "/es/thank-you-saint" : "/thank-you-saint");

  const [step, setStep] = useState(1);
  const [diagnosed, setDiagnosed] = useState("");
  const [triedMeds, setTriedMeds] = useState("");
  const [state, setState] = useState("");
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const googleAdsCustomFields = (p: GoogleAdsParams) =>
    googleAds
      ? {
          [GOOGLE_ADS_FIELD_IDS.leadSource]: {
            value: p.leadSource,
            label: "Lead Source",
          },
          [GOOGLE_ADS_FIELD_IDS.landingPage]: {
            value: p.landingPage,
            label: "Landing Page",
          },
          [GOOGLE_ADS_FIELD_IDS.gclid]: { value: p.gclid, label: "GCLID" },
          [GOOGLE_ADS_FIELD_IDS.gbraid]: { value: p.gbraid, label: "GBRAID" },
          [GOOGLE_ADS_FIELD_IDS.wbraid]: { value: p.wbraid, label: "WBRAID" },
          [GOOGLE_ADS_FIELD_IDS.utmSource]: {
            value: p.utmSource,
            label: "UTM Source",
          },
          [GOOGLE_ADS_FIELD_IDS.utmMedium]: {
            value: p.utmMedium,
            label: "UTM Medium",
          },
          [GOOGLE_ADS_FIELD_IDS.utmCampaign]: {
            value: p.utmCampaign,
            label: "UTM Campaign",
          },
          [GOOGLE_ADS_FIELD_IDS.utmTerm]: {
            value: p.utmTerm,
            label: "UTM Term",
          },
          [GOOGLE_ADS_FIELD_IDS.utmContent]: {
            value: p.utmContent,
            label: "UTM Content",
          },
          [GOOGLE_ADS_FIELD_IDS.contactTag]: {
            value: GOOGLE_ADS_CONTACT_TAG,
            label: "Contact Tag",
          },
        }
      : {};

  const eligible = !(diagnosed === "no" || triedMeds === "no");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const formId = source;
    const formName = `SAINT Eligibility Check — ${source}`;
    const payload = buildTrackingPayload(formId, formName);
    payload.formData = {
      first_name: firstName,
      phone,
      email,
      calendar_notes: s.notesTemplate(diagnosed, triedMeds, state),
    };
    payload.formLabels = s.formLabels;

    // Fires GA4 generate_lead + Google Ads "SAINT Lead Form Submit"
    // conversion + Meta Pixel Lead EXACTLY ONCE. Do not also call trackLead().
    trackLeadConversion(formId, formName);

    try {
      await postTrackingEvent(payload, {
        customFields: googleAdsCustomFields(adsParams),
      });
    } catch {
      /* fire-and-forget — still redirect */
    }
    const qs = eligible ? "" : "?eligible=no";
    navigate(`${finalRedirectTo}${qs}`);
  };

  const isCard = variant === "card";

  return (
    <div
      id="saint-eligibility-form"
      className={
        isCard
          ? "rounded-2xl border border-border bg-card p-6 text-foreground shadow-soft sm:p-8"
          : "text-foreground"
      }
    >
      <div className="mb-5">
        <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
          {finalHeading}
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {finalSubheading}
        </p>
      </div>

      {/* Step indicator */}
      <div className="mb-6 flex items-center gap-2">
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              step >= n ? "bg-accent" : "bg-border"
            }`}
          />
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* STEP 1 — diagnosed with depression */}
        {step === 1 && (
          <div className="space-y-3">
            <Label className="text-base font-semibold text-foreground">
              {s.q1}
            </Label>
            <RadioGroup
              value={diagnosed}
              onValueChange={setDiagnosed}
              className="grid gap-2.5"
            >
              {s.q1Opts.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                    diagnosed === opt.value
                      ? "border-accent bg-accent/10"
                      : "border-border hover:border-accent/50"
                  }`}
                >
                  <RadioGroupItem value={opt.value} id={`d-${opt.value}`} />
                  <span className="text-sm font-medium text-foreground">
                    {opt.label}
                  </span>
                </label>
              ))}
            </RadioGroup>
            <Button
              type="button"
              size="lg"
              disabled={!diagnosed}
              onClick={() => setStep(2)}
              className="w-full bg-gradient-accent text-accent-foreground font-bold shadow-glow hover:opacity-90"
            >
              {s.continue} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* STEP 2 — tried antidepressants without enough relief */}
        {step === 2 && (
          <div className="space-y-3">
            <Label className="text-base font-semibold text-foreground">
              {s.q2}
            </Label>
            <RadioGroup
              value={triedMeds}
              onValueChange={setTriedMeds}
              className="grid gap-2.5"
            >
              {s.q2Opts.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                    triedMeds === opt.value
                      ? "border-accent bg-accent/10"
                      : "border-border hover:border-accent/50"
                  }`}
                >
                  <RadioGroupItem value={opt.value} id={`m-${opt.value}`} />
                  <span className="text-sm font-medium text-foreground">
                    {opt.label}
                  </span>
                </label>
              ))}
            </RadioGroup>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                {s.back}
              </Button>
              <Button
                type="button"
                size="lg"
                disabled={!triedMeds}
                onClick={() => setStep(3)}
                className="flex-1 bg-gradient-accent text-accent-foreground font-bold shadow-glow hover:opacity-90"
              >
                {s.continue} <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — state + contact */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`elig-state-${source}`}>{s.q3}</Label>
              <Select value={state} onValueChange={setState} required>
                <SelectTrigger id={`elig-state-${source}`}>
                  <SelectValue placeholder={s.statePlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((st) => (
                    <SelectItem key={st.value} value={st.value}>
                      {st.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor={`elig-first-${source}`}>{s.firstName}</Label>
              <Input
                id={`elig-first-${source}`}
                required
                placeholder={s.firstNamePlaceholder}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`elig-phone-${source}`}>{s.phone}</Label>
              <Input
                id={`elig-phone-${source}`}
                type="tel"
                required
                placeholder={s.phonePlaceholder}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`elig-email-${source}`}>{s.email}</Label>
              <Input
                id={`elig-email-${source}`}
                type="email"
                required
                placeholder={s.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <label className="flex items-start gap-2.5 text-sm text-muted-foreground">
              <input
                type="checkbox"
                required
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-accent accent-accent"
              />
              <span>
                {s.consent}
                <a
                  href="/privacy-policy"
                  className="font-medium text-accent underline underline-offset-2 hover:text-accent/80"
                >
                  {s.privacyPolicy}
                </a>
                {s.consentEnd}
              </span>
            </label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => setStep(2)}
                className="flex-1"
              >
                {s.back}
              </Button>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="flex-1 bg-gradient-accent text-accent-foreground font-bold shadow-glow hover:opacity-90"
              >
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                {submitting ? s.sending : s.submit}
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            {s.disclaimer}
          </p>
        </div>
      </form>
    </div>
  );
};

export default SaintEligibilityCheck;
